import { Injectable } from '@angular/core'
import { ActivatedRouterStateSnapshot, PageType, ProductSearchPage, RoutingService, } from '@spartacus/core'
import { Observable } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import { FacetList, ProductFacetService, ProductListComponentService } from '@spartacus/storefront'
import { MbConstants } from '../../shared/mb-constants'

/**
 * Provides access to all the facets and active facets for the Product Listing Page.
 */
@Injectable({
  providedIn: 'root',
})
export class MbProductFacetService extends ProductFacetService {

  constants = MbConstants

  constructor (protected routing: RoutingService,
              protected productListComponentService: ProductListComponentService) {
    super(routing, productListComponentService)
  }

  /**
   * Returns the search results for the current page.
   */
  protected readonly mbSearchResult$: Observable<ProductSearchPage> =
    this.routeState$.pipe(
      switchMap((state: ActivatedRouterStateSnapshot) =>
        this.productListComponentService.model$.pipe(
          filter((page: ProductSearchPage) => this.filterForPage(state, page)),
          map((page: ProductSearchPage) => ({
            ...page,
            breadcrumbs: this.filterBreadcrumbs(
              page?.breadcrumbs ?? [],
              state.params
            ),
          }))
        )
      )
    )

  /**
   * Observes the facets and active facets for the given page. The facet data
   * is provided in a `FacetList`.
   */
  readonly facetList$: Observable<FacetList> = this.mbSearchResult$.pipe(
    map(
      (result: ProductSearchPage) =>
        ({
          facets: result.facets,
          activeFacets: result.breadcrumbs,
        } as FacetList)
    )
  )

  /**
   * Filters the current result by verifying if the result is related to the page.
   * This is done to avoid a combination of the next page and the current search results.
   */
  protected filterForPage (state: ActivatedRouterStateSnapshot, page: ProductSearchPage): boolean {
    if (!page.currentQuery?.query?.value) {
      return false
    }

    if (state.context.type === PageType.CATEGORY_PAGE) {
      return (page.currentQuery.query.value.indexOf(`allCategories:${state.context.id}`) > -1)
    }

    if (state.context.type === PageType.PRODUCT_PAGE) {
      return (page.currentQuery.query.value.toLowerCase().indexOf(state?.queryParams?.query?.toLowerCase()) > -1)
    }

    if (state.context.type === PageType.CONTENT_PAGE && (state.context.id === 'search' || state.context.id === '/search')) {
      return page.currentQuery.query.value.toLowerCase().startsWith(`${String(state.params.query).toLowerCase()}:`)
        || page.currentQuery.query.value.toLowerCase().startsWith(this.constants.COLON_RELEVANCE.toLowerCase())
    }

    return false
  }
}
